/**
 * File: assets/breed-modal.core.js
 * Core logic:
 * - storage
 * - normalization
 * - breeds loading + size detection (single source of truth map)
 * - fetching products from filtered collection HTML
 * - resolving correct variant + price for chosen size
 */
(() => {
  const NS = '[BreedModal/Core]';
  const log = (...args) => console.log(NS, ...args);

  /* I18N-06 (#163): visible strings come from the page; the English literal is the fallback. */
  const wfStrings = (() => {
    try {
      const node = document.querySelector('script[type="application/json"][data-wf-i18n="breed-modal"]');
      const parsed = node ? JSON.parse(node.textContent) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  })();
  // Shopify's `t` HTML-escapes every translation whose key does not end in `_html` (French
  // "n'a" arrives as "n&#39;a"), so the page's values are decoded back to plain text.
  const wfDecode = (text) =>
    text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (entity, body) => {
      const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[body.toLowerCase()];
      if (named) return named;
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
  const tr = (key, fallback, values = {}) => {
    const text = typeof wfStrings[key] === 'string' && wfStrings[key] !== '' ? wfDecode(wfStrings[key]) : fallback;
    return Object.keys(values).reduce((out, name) => out.split(`{${name}}`).join(values[name]), text);
  };

  // -------------------------
  // Storage (safe)
  // -------------------------
  function safeGet(key) {
    try {
      return localStorage.getItem(key) || '';
    } catch {
      return '';
    }
  }
  function safeSet(key, val) {
    try {
      localStorage.setItem(key, val);
    } catch {}
  }
  function safeRemove(key) {
    try {
      localStorage.removeItem(key);
    } catch {}
  }

  /* GAP4-11 (#341): the breed a visitor picks used to stay in this browser's localStorage and go
     nowhere else, while s-modal-gratitude thanked them for sharing it. setSelectedBreed - the one
     place a pick is stored - now also announces it on the document as 'wf:breed-selected' with
     detail { breed }, the stored display name. assets/woopf-lifecycle-events.js turns that into a
     pet_breed_selected dataLayer event and, with marketing consent only, a DogBreed property on
     the visitor's Klaviyo profile. Reading a stored breed back (getSelectedBreed, the modal
     re-opening on it) never announces. Guarded: a browser without CustomEvent, or a listener that
     throws, must not stop the breed being chosen. */
  function announceBreed(breed) {
    try {
      if (typeof CustomEvent !== 'function') return;
      document.dispatchEvent(new CustomEvent('wf:breed-selected', { detail: { breed } }));
    } catch {}
  }

  // -------------------------
  // Normalization
  // -------------------------
  const norm = (s) => String(s || '').trim();

  function normSearch(s) {
    return norm(s)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/[–—]/g, '-')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function uniqueSorted(list) {
    const set = new Set();
    (list || []).forEach((x) => {
      const v = norm(x);
      if (v) set.add(v);
    });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  // -------------------------
  // Breeds helpers
  // -------------------------
  function safeJsonParse(s) {
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }

  function flattenGroupedBreeds(parsed) {
    const buckets = ['small', 'medium', 'large', 'extra_large'];
    const out = [];
    buckets.forEach((k) => {
      const arr = parsed?.[k];
      if (Array.isArray(arr)) out.push(...arr);
    });
    return out;
  }

  function buildBreedSizeMap(groupedJson) {
    // IMPORTANT:
    // Use a fixed priority so one breed can't "jump" buckets due to duplicates.
    // If a breed exists in multiple arrays, first match wins.
    const map = Object.create(null);
    const buckets = ['small', 'medium', 'large', 'extra_large'];
    for (const k of buckets) {
      const arr = groupedJson?.[k];
      if (!Array.isArray(arr)) continue;
      for (const breed of arr) {
        const key = normSearch(breed);
        if (key && map[key] == null) map[key] = k;
      }
    }
    return map;
  }

  // -------------------------
  // Collection parsing helpers
  // -------------------------
  function pickBestSrcFromSrcset(srcset) {
    const parts = String(srcset || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return '';
    const last = parts[parts.length - 1];
    const url = last.split(' ')[0].trim();
    return url || '';
  }

  function normalizeImgUrl(u) {
    let img = String(u || '').trim();
    if (!img) return '';
    if (img.startsWith('//')) img = `https:${img}`;
    if (img.startsWith('/')) img = `${location.origin}${img}`;
    return img;
  }

  function cleanPriceText(t) {
    const s = String(t || '')
      .replace(/\s+/g, ' ')
      .replace(/Regular price|Sale price|From|Unit price/gi, '')
      .trim();

    /* Breed results (owner, 2026-10-29): any currency sign (\p{Sc}), not only the seven once
       listed. Without the peso sign no pattern matched and the whole price row was kept. */
    const m = s.match(/(\p{Sc}|AUD|USD|EUR|GBP)\s?[\d.,]+/iu);
    if (m && m[0]) return m[0].trim();

    const m2 = s.match(/[\d.,]+\s?\p{Sc}/u);
    if (m2 && m2[0]) return m2[0].trim();

    if (/\d/.test(s)) return s;
    return '';
  }

  /* Breed results (owner, 2026-10-29): an element's text without its `hidden` parts. The bed
     finder's card keeps a "Sold out" flag in its price row, hidden while the bed is in stock, and
     textContent reads hidden text too. */
  function visibleText(el) {
    if (!el) return '';
    const copy = typeof el.cloneNode === 'function' ? el.cloneNode(true) : null;
    if (!copy || typeof copy.querySelectorAll !== 'function') return el.textContent || '';
    copy.querySelectorAll('[hidden]').forEach((node) => node.remove());
    return copy.textContent || '';
  }

  function getHandleFromUrl(productUrl) {
    try {
      const u = new URL(productUrl, location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      return '';
    } catch {
      // fallback: "/products/handle"
      const m = String(productUrl || '').match(/\/products\/([^/?#]+)/);
      return m?.[1] || '';
    }
  }

  /* I18N-06 (#163): the storefront's language root - "/" in the primary language, "/de/", "/ja/"
     and so on elsewhere (Shopify writes it to window.Shopify.routes.root). The finder asked for its
     collections and products without it, so on /de its cards came back in the primary language.
     A path that already starts with the root, or is not site-relative, is left as it is. */
  function localeRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== 'string' || root.charAt(0) !== '/') return '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function localePath(path) {
    const value = String(path || '');
    const root = localeRoot();
    if (root === '/' || value.charAt(0) !== '/' || value.startsWith('//') || value.startsWith(root)) return value;
    return `${root}${value.slice(1)}`;
  }

  function formatMoneyFromCents(cents, currency) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    const curr = currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: curr || 'USD',
        currencyDisplay: 'symbol',
      }).format(n / 100);
    } catch {
      // fallback
      return `${(n / 100).toFixed(2)}${curr ? ` ${curr}` : ''}`;
    }
  }

  // -------------------------
  // Public API (core)
  // -------------------------
  function createCore(config) {
    const {
      shownKey,
      selectedBreedKey,
      breedsUrl,
      breedsUrlFallback,
      weightBreedsUrl,
      bulkBreedsUrl,
      fallbackBreeds,
      bedSizeUrls,
      gearSizeUrls,
      playSizeUrls,
      sizeOptionValueMap,
      collectionGridSectionId,
      variantOptionNameHints,
      maxVariantFetch = 30,
    } = config;

    let breedsGrouped = null;
    let breedSizeMap = null;

    let weightBreedsGrouped = null;
    let breedWeightSizeMap = null;

    let bulkBreedsGrouped = null;
    let breedBulkSizeMap = null;

    // cache product json by handle
    const productJsonCache = new Map();
    const productJsonInflight = new Map();
 
    // cache collection json by handle
    const collectionProductsCache = new Map();
    const collectionProductsInflight = new Map();

    function alreadyShown() {
      return safeGet(shownKey) === '1';
    }
    function markShown() {
      safeSet(shownKey, '1');
    }
    function getSelectedBreed() {
      return safeGet(selectedBreedKey);
    }
    function setSelectedBreed(breed) {
      safeSet(selectedBreedKey, breed);
      /* GAP4-11 (#341): announced even when storage is blocked - the visitor still chose it */
      announceBreed(breed);
    }
    function removeSelectedBreed() {
      safeRemove(selectedBreedKey);
    }

    async function loadBreeds() {
      const tryFetch = async (url) => {
        const res = await fetch(url, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const parsed = safeJsonParse(text);

        if (Array.isArray(parsed?.breeds)) {
          breedsGrouped = null;
          breedSizeMap = null;
          return { list: parsed.breeds, grouped: null };
        }

        const flat = flattenGroupedBreeds(parsed);
        if (flat.length) {
          breedsGrouped = parsed;
          breedSizeMap = buildBreedSizeMap(parsed);
          return { list: flat, grouped: parsed };
        }

        throw new Error('Invalid JSON format');
      };

      try {
        return await tryFetch(breedsUrl);
      } catch (e1) {
        try {
          return await tryFetch(breedsUrlFallback);
        } catch (e2) {
          log('Failed to load breeds list, using fallback list', e1, e2);
          breedsGrouped = null;
          breedSizeMap = null;
          return { list: fallbackBreeds || [], grouped: null };
        }
      }
    }

    async function loadWeightBreeds() {
      try {
        const res = await fetch(weightBreedsUrl, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const text = await res.text();
        const parsed = safeJsonParse(text);

        const flat = flattenGroupedBreeds(parsed);

        if (flat.length) {
          weightBreedsGrouped = parsed;
          breedWeightSizeMap = buildBreedSizeMap(parsed);
          return { list: flat, grouped: parsed };
        }

        throw new Error('Invalid weight JSON format');
      } catch (e) {
        log('Failed to load weight breeds list', e);
        weightBreedsGrouped = null;
        breedWeightSizeMap = null;
        return { list: [], grouped: null };
      }
    }

    async function loadBulkBreeds() {
      try {
        const res = await fetch(bulkBreedsUrl, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const text = await res.text();
        const parsed = safeJsonParse(text);

        const flat = flattenGroupedBreeds(parsed);

        if (flat.length) {
          bulkBreedsGrouped = parsed;
          breedBulkSizeMap = buildBreedSizeMap(parsed);
          return { list: flat, grouped: parsed };
        }

        throw new Error('Invalid bulk JSON format');
      } catch (e) {
        log('Failed to load bulk breeds list', e);
        bulkBreedsGrouped = null;
        breedBulkSizeMap = null;
        return { list: [], grouped: null };
      }
    }

    function detectSizeForBreed(breed) {
      const key = normSearch(breed);
      if (!key) return '';
      if (breedSizeMap && breedSizeMap[key]) return breedSizeMap[key];

      // fallback scan
      if (!breedsGrouped) return '';
      const buckets = ['small', 'medium', 'large', 'extra_large'];
      for (const k of buckets) {
        const arr = breedsGrouped?.[k];
        if (!Array.isArray(arr)) continue;
        if (arr.some((x) => normSearch(x) === key)) return k;
      }
      return '';
    }

    function detectWeightSizeForBreed(breed) {
      const key = normSearch(breed);
      if (!key) return '';

      if (breedWeightSizeMap && breedWeightSizeMap[key]) {
        return breedWeightSizeMap[key];
      }

      if (!weightBreedsGrouped) return '';

      const buckets = ['small', 'medium', 'large'];

      for (const k of buckets) {
        const arr = weightBreedsGrouped?.[k];
        if (!Array.isArray(arr)) continue;

        if (arr.some((x) => normSearch(x) === key)) return k;
      }

      return '';
    }

    function detectBulkSizeForBreed(breed) {
      const key = normSearch(breed);
      if (!key) return '';

      if (breedBulkSizeMap && breedBulkSizeMap[key]) {
        return breedBulkSizeMap[key];
      }

      if (!bulkBreedsGrouped) return '';

      const buckets = ['medium', 'large'];

      for (const k of buckets) {
        const arr = bulkBreedsGrouped?.[k];
        if (!Array.isArray(arr)) continue;

        if (arr.some((x) => normSearch(x) === key)) return k;
      }

      return '';
    }

    function getSizeOptionValue(sizeKey) {
      return sizeOptionValueMap?.[sizeKey] || '';
    }

    function urlWithSections(url, sectionId) {
      /* I18N-06 (#163): in the page's language (this path has no caller today) */
      const u = new URL(localePath(url), window.location.origin);
      u.searchParams.set('sections', sectionId);
      return u.toString();
    }

    async function fetchCollectionSectionHTML(collectionUrl) {
      const requestUrl = urlWithSections(collectionUrl, collectionGridSectionId);
      log('Fetching collection section:', requestUrl);

      const res = await fetch(requestUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const keys = Object.keys(json || {});
      log('Sections JSON keys:', keys);

      const hasKey = Object.prototype.hasOwnProperty.call(json || {}, collectionGridSectionId);
      let html = hasKey ? json[collectionGridSectionId] : '';
      if (!hasKey && keys.length) {
        html = json[keys[0]] || '';
        log('SectionId key not found. Using first returned key:', keys[0]);
      }

      const len = typeof html === 'string' ? html.length : 0;
      log('Section HTML length:', len);

      return typeof html === 'string' ? html : '';
    }

    function findProductGridRoot(doc) {
      // Prefer actual product grid to avoid “hammock appears though filter excludes it”.
      return (
        /* Breed results (owner, 2026-10-29): the redesigned collection grid's results, which the
           size in the URL filters. Without it the whole <main> was read, the bed finder's cards
           too, which ignore the size: an XL breed was shown 18 beds, 3 of them its size. */
        doc.querySelector('[data-wf-grid-swap="results"]') ||
        doc.querySelector('#ProductGridContainer') ||
        doc.querySelector('[id*="ProductGridContainer"]') ||
        doc.querySelector('.product-grid-container') ||
        doc.querySelector('.product-grid') ||
        doc.querySelector('.grid.product-grid') ||
        doc.querySelector('main') ||
        doc.body
      );
    }

    async function fetchCollectionProductsFallback(collectionUrl, limit = 30) {
      const cacheKey = `${collectionUrl}::${limit}`;

      if (collectionProductsCache.has(cacheKey)) {
        return collectionProductsCache.get(cacheKey);
      }

      if (collectionProductsInflight.has(cacheKey)) {
        return collectionProductsInflight.get(cacheKey);
      }

      const request = (async () => {
        log('Fallback: fetching full collection HTML:', collectionUrl);

        /* I18N-06 (#163): the collection in the page's language, so its product links keep it */
        const requestUrl = new URL(localePath(collectionUrl), window.location.href).toString();

        const res = await fetch(requestUrl, {
          credentials: 'include',
          cache: 'no-store',
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const root = findProductGridRoot(doc);

        let anchors = Array.from(root.querySelectorAll('a[href*="/products/"]'));

        if (!anchors.length) {
          anchors = Array.from(doc.querySelectorAll('a[href*="/products/"]'));
        }

        const products = [];
        const seen = new Set();

        for (const a of anchors) {
          const hrefRaw = a.getAttribute('href') || '';
          if (!hrefRaw.includes('/products/')) continue;

          const cleanHref = hrefRaw.split('?')[0];
          const url = cleanHref.startsWith('http') ? cleanHref : `${location.origin}${cleanHref}`;
          if (seen.has(url)) continue;

          const card =
            a.closest('[data-product-id]') ||
            a.closest('.card-wrapper') ||
            a.closest('.card') ||
            a.closest('.grid__item') ||
            a.closest('li') ||
            a.parentElement;

          if (!card) continue;

          const hasPriceHint =
            !!card.querySelector('.price') ||
            !!card.querySelector('.price-item') ||
            !!card.querySelector('[class*="price"]');

          const hasImageHint = !!card.querySelector('img');

          if (!hasPriceHint && !hasImageHint) continue;

          const imgEl = card.querySelector('img') || a.querySelector('img') || null;

          let img = '';
          if (imgEl) {
            img =
              imgEl.getAttribute('src') ||
              imgEl.getAttribute('data-src') ||
              pickBestSrcFromSrcset(imgEl.getAttribute('srcset')) ||
              pickBestSrcFromSrcset(imgEl.getAttribute('data-srcset')) ||
              '';

            img = normalizeImgUrl(img);
          }

          let title =
            /* Breed results (owner, 2026-10-29): the redesigned grid card's name; its first link is
               the picture's, with no words */
            (card.querySelector('.wf-product-card__title')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__heading a')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__heading')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (card.querySelector('.card__information .full-unstyled-link')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim() ||
            (card.querySelector('.full-unstyled-link')?.textContent || '').replace(/\s+/g, ' ').trim() ||
            (a.textContent || '').replace(/\s+/g, ' ').trim();

          if (!title || title.toLowerCase() === 'new') {
            /* I18N-06 (#163): the fallback title, translated ('new' above is the store's badge text, read as data) */
            title = tr('view_product', 'View product');
          }

          const priceEl =
            card.querySelector('.price-item--regular') ||
            card.querySelector('.price-item') ||
            card.querySelector('.price') ||
            card.querySelector('.money') ||
            card.querySelector('[class*="price"]');

          /* Breed results (owner, 2026-10-29): the words the card shows, not its hidden flags */
          const price = cleanPriceText(visibleText(priceEl));

          seen.add(url);
          products.push({ title, url, img, price });

          if (products.length >= limit) break;
        }

        collectionProductsCache.set(cacheKey, products);

        return products;
      })();

      collectionProductsInflight.set(cacheKey, request);

      try {
        return await request;
      } finally {
        collectionProductsInflight.delete(cacheKey);
      }
    }

    /* I18N-06 (#163): asked for under a language root - the page's by default, "/" for the
       primary language - and cached per root and handle */
    async function fetchProductJsonByHandle(handle, root = localeRoot()) {
      if (!handle) return null;

      const cacheKey = `${root}${handle}`;

      if (productJsonCache.has(cacheKey)) return productJsonCache.get(cacheKey);

      if (productJsonInflight.has(cacheKey)) return productJsonInflight.get(cacheKey);

      const p = (async () => {
        const url = `${location.origin}${root}products/${handle}.js`;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        productJsonCache.set(cacheKey, json);
        return json;
      })();

      productJsonInflight.set(cacheKey, p);

      try {
        return await p;
      } finally {
        productJsonInflight.delete(cacheKey);
      }
    }

    function pickVariantForSize(productJson, desiredSizeValue) {
      if (!productJson || !Array.isArray(productJson.variants) || !desiredSizeValue) return null;

      // Try to find which option index represents size
      // Shopify product JSON exposes:
      // - options: ["Size", "Color"] (names)
      // - variants[i].options: ["Large", "Black"] (values)
      const optionNames = Array.isArray(productJson.options) ? productJson.options : [];
      let sizeIndex = -1;

      // Prefer matching by name hints
      const hints = Array.isArray(variantOptionNameHints) && variantOptionNameHints.length ? variantOptionNameHints : ['size'];
      for (let i = 0; i < optionNames.length; i++) {
        const name = String(optionNames[i] || '').toLowerCase();
        if (hints.some((h) => name.includes(String(h).toLowerCase()))) {
          sizeIndex = i;
          break;
        }
      }

      // If we didn't find, fallback to first option
      if (sizeIndex < 0) sizeIndex = 0;

      const desired = String(desiredSizeValue).trim().toLowerCase();

      // Try exact match on that option position
      const exact = productJson.variants.find((v) => {
        const opts = Array.isArray(v.options) ? v.options : [];
        const val = String(opts[sizeIndex] || '').trim().toLowerCase();
        return val === desired;
      });
      if (exact) return exact;

      // Fallback: any option matches desired
      const any = productJson.variants.find((v) => {
        const opts = Array.isArray(v.options) ? v.options : [];
        return opts.some((x) => String(x || '').trim().toLowerCase() === desired);
      });
      return any || null;
    }

    async function enrichProductsWithSizeVariant(products, sizeKey) {
      const desiredSizeValue = getSizeOptionValue(sizeKey);
      if (!desiredSizeValue) return products || [];

      const list = Array.isArray(products) ? products.slice(0, maxVariantFetch) : [];
      const rest = Array.isArray(products) ? products.slice(maxVariantFetch) : [];

      const out = await Promise.all(
        list.map(async (p) => {
          const handle = getHandleFromUrl(p.url);

          if (!handle) {
            return { ...p };
          }

          try {
            const json = await fetchProductJsonByHandle(handle);
            let variant = pickVariantForSize(json, desiredSizeValue);

            /* I18N-06 (#163): under a language root the product comes back translated, option
               values too ("Klein" for "Small"), while the size map is English. When nothing
               matches, find the size in the primary-language copy and take the same variant, by
               id, from the translated one, whose title the card shows. */
            if (!variant && localeRoot() !== '/') {
              try {
                const primary = await fetchProductJsonByHandle(handle, '/');
                const match = pickVariantForSize(primary, desiredSizeValue);
                if (match) variant = (json?.variants || []).find((item) => item.id === match.id) || match;
              } catch (error) {
                // No primary copy: the card keeps the translated title, without a size.
              }
            }

            if (variant) {
              const currency =
                (window.Shopify && Shopify.currency && Shopify.currency.active) ||
                (window.Shopify && Shopify.currency && Shopify.currency.default) ||
                '';

              const sizePriceText =
                typeof window.Shopify?.formatMoney === 'function'
                  ? Shopify.formatMoney(variant.price, window.Shopify?.money_format || undefined)
                  : formatMoneyFromCents(variant.price, currency);

              return {
                ...p,
                /* I18N-06 (#163) */
                title: json?.title || p.title || tr('view_product', 'View product'),
                variantId: variant.id,
                price: p.price || cleanPriceText(sizePriceText) || sizePriceText,
                url: `${p.url.split('?')[0]}?variant=${variant.id}`,
                sizeValue: desiredSizeValue,
              };
            }

            return {
              ...p,
              /* I18N-06 (#163) */
              title: json?.title || p.title || tr('view_product', 'View product'),
            };
          } catch (e) {
            return { ...p };
          }
        })
      );

      return [...out, ...rest];
    }

    async function getProductsForBreed(breed) {
      const bedSizeKey = detectSizeForBreed(breed);
      const gearSizeKey = detectWeightSizeForBreed(breed);
      const playSizeKey = detectBulkSizeForBreed(breed);

      const sources = [];

      const bedUrl = bedSizeUrls?.[bedSizeKey];
      if (bedUrl) {
        sources.push({
          key: 'beds',
          /* I18N-06 (#163): the card's category chip - display text; `key` stays the data */
          label: tr('category_beds', 'Sleep & Comfort'),
          sizeKey: bedSizeKey,
          url: bedUrl,
        });
      }

      const gearUrl = gearSizeUrls?.[gearSizeKey];
      if (gearUrl) {
        sources.push({
          key: 'gear',
          /* I18N-06 (#163) */
          label: tr('category_gear', 'Walking Gear'),
          sizeKey: gearSizeKey,
          url: gearUrl,
        });
      }

      const playUrl = playSizeUrls?.[playSizeKey];
      if (playUrl) {
        sources.push({
          key: 'toys',
          /* I18N-06 (#163) */
          label: tr('category_toys', 'Play & Enrichment'),
          sizeKey: playSizeKey,
          url: playUrl,
        });
      }

      return {
        bedSizeKey,
        gearSizeKey,
        playSizeKey,
        sources,
      };
    }

    return {
      normSearch,
      uniqueSorted,

      alreadyShown,
      markShown,
      getSelectedBreed,
      setSelectedBreed,
      removeSelectedBreed,

      loadBreeds,
      loadWeightBreeds,
      loadBulkBreeds,
      detectSizeForBreed,
      detectWeightSizeForBreed,
      detectBulkSizeForBreed,
      getProductsForBreed,
      getSizeOptionValue,

      fetchCollectionSectionHTML,
      fetchCollectionProductsFallback,
      enrichProductsWithSizeVariant,
    };
  }

  window.WOOPF_BREED_MODAL_CORE = { createCore };
})();
